/**
 * Framework registry loader (G2-01, HG-2A).
 *
 * The single read path for the competency framework registry created by
 * migration 232 and seeded by 233. Resolves a framework version by the
 * (owning_tenant, version_identifier) pair, where a NULL tenant means the
 * WingMentor reference model, and returns a typed, ordered, cached result.
 *
 * Load-bearing registry facts (verified on val, 15 August 2026):
 *  - Lifecycle status values are 'draft' | 'published' | 'expired'. There is
 *    no 'active'. Published and expired versions are both resolvable
 *    (historical assessments must still resolve, GCH-18); draft never is.
 *  - The Framework Listing Order lives on framework_versions.listing_order
 *    (TEXT[], OPR always last). Competencies carry no order column: ordering
 *    is array_position over the version row's listing_order, and codes absent
 *    from the competency set simply do not appear. Never sort alphabetically.
 *  - competencies.title is the authoritative ICAO title; wm_label is the
 *    nullable WingMentor display label.
 *  - origin values are 'reference' | 'operator_derived' | 'operator_competency';
 *    only operator_derived rows carry parent_behaviour_id (DB CHECK iff).
 */
import {
  CompetencyRowV1,
  FrameworkDimensionScaleRowV1,
  FrameworkGradeScaleRowV1,
  FrameworkVersionRowV1,
  ObservableBehaviourRowV1,
} from './types';
import type { CompetencyRow, FrameworkVersionRow, ObservableBehaviourRow } from './types';

// ---------------------------------------------------------------------------
// Minimal structural client
// ---------------------------------------------------------------------------
// The narrowest surface of a Supabase (PostgREST) client the loader needs.
// All three platforms' clients satisfy this structurally, so the loader does
// not couple to any one @supabase/supabase-js version, and tests can pass a
// hand-rolled mock.

export interface FrameworkQueryResult {
  data: unknown;
  error: { message: string } | null;
}

export interface FrameworkSelectBuilder extends PromiseLike<FrameworkQueryResult> {
  eq(column: string, value: unknown): FrameworkSelectBuilder;
  is(column: string, value: null): FrameworkSelectBuilder;
  in(column: string, values: readonly unknown[]): FrameworkSelectBuilder;
  maybeSingle(): PromiseLike<FrameworkQueryResult>;
}

export interface FrameworkDataClient {
  from(table: string): { select(columns: string): FrameworkSelectBuilder };
}

// ---------------------------------------------------------------------------
// Resolved shapes (the typed result; consumers never see a raw row)
// ---------------------------------------------------------------------------

export interface ResolvedBehaviour {
  code: string;
  /** ICAO wording, authoritative, read from the registry (GCH-06). */
  behaviour: string;
  icaoRef: string | null;
  origin: 'reference' | 'operator_derived' | 'operator_competency';
  /** Code of the parent behaviour; set only when origin is operator_derived. */
  parentCode: string | null;
  ksaDimension: 'knowledge' | 'skill' | 'attitude';
  observableIndicator: string | null;
  status: 'published' | 'expired';
}

export interface ResolvedCompetency {
  code: string;
  /** Authoritative ICAO title (competencies.title). */
  title: string;
  /** WingMentor display label; null means fall back to title (GCH-05). */
  wmLabel: string | null;
  scope: 'reference' | 'operator';
  /** Mandatory for an Operator Competency (CFX-13/GCH-22), else null. */
  justification: string | null;
  /** 1-based position within the version's Framework Listing Order. */
  listingPosition: number;
  status: 'published' | 'expired';
  behaviours: ResolvedBehaviour[];
}

export interface ResolvedGradeStep {
  value: number;
  label: string;
  meaning: string | null;
}

export interface ResolvedDimensionStep {
  dimension: 'how_many' | 'how_often' | 'tem_outcome';
  /** 1 (worst) to 5 (best). */
  position: number;
  code: string;
  wordPicture: string;
}

export interface ResolvedFramework {
  frameworkId: string;
  versionIdentifier: string;
  versionLabel: string | null;
  status: 'published' | 'expired';
  /** null = the WingMentor reference model. */
  owningTenant: string | null;
  /** The version row's Framework Listing Order, OPR always last. */
  listingOrder: string[];
  /** Competencies in Framework Listing Order. */
  competencies: ResolvedCompetency[];
  /** Convenience: competency codes in Framework Listing Order. */
  competencyCodes: string[];
  /** Convenience: every behaviour code across all competencies. */
  behaviourCodes: string[];
  /** Grade scale for this version, ordered 1 to 5. */
  gradeScale: ResolvedGradeStep[];
  /** Dimension word pictures, ordered by dimension then position. */
  dimensionScales: ResolvedDimensionStep[];
}

export interface LoadFrameworkOptions {
  /** Owning tenant UUID; null or undefined resolves the reference model. */
  tenant?: string | null;
  /** The immutable opaque publication identifier (CFX-01). */
  versionIdentifier: string;
}

// ---------------------------------------------------------------------------
// Cache
// ---------------------------------------------------------------------------
// Published and expired version content is frozen by migration 232's content
// guard (CFX-13: replaced by a successor version, never edited), so a
// resolved framework can be cached for the process lifetime. Draft versions
// are never resolvable, so nothing mutable is ever cached. The cache stores
// the promise so one render issues one query set, never N (G2-01.1).

const cache = new Map<string, Promise<ResolvedFramework>>();

function cacheKey(tenant: string | null | undefined, versionIdentifier: string): string {
  return `${tenant ?? 'reference'}::${versionIdentifier}`;
}

/** Test hook: empty the loader cache. */
export function clearFrameworkCache(): void {
  cache.clear();
}

// ---------------------------------------------------------------------------
// Loader
// ---------------------------------------------------------------------------

/** Statuses a resolvable version or content row may carry. Never 'draft'. */
const RESOLVABLE_STATUSES = ['published', 'expired'] as const;

class FrameworkLoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FrameworkLoadError';
  }
}

function rows(result: FrameworkQueryResult, context: string): unknown[] {
  if (result.error) {
    throw new FrameworkLoadError(`${context}: ${result.error.message}`);
  }
  return Array.isArray(result.data) ? result.data : [];
}

/**
 * Resolve one framework version and its full content.
 *
 * Draft versions are not resolvable and reject with FrameworkLoadError.
 * Published and expired versions both resolve (GCH-18). The result is
 * cached per (tenant, versionIdentifier) for the process lifetime.
 */
export function loadFrameworkVersion(
  client: FrameworkDataClient,
  options: LoadFrameworkOptions,
): Promise<ResolvedFramework> {
  const key = cacheKey(options.tenant, options.versionIdentifier);
  const cached = cache.get(key);
  if (cached) return cached;

  const loading = loadUncached(client, options).catch((err) => {
    // Never cache a failure: a transient error must not poison the key.
    cache.delete(key);
    throw err;
  });
  cache.set(key, loading);
  return loading;
}

async function loadUncached(
  client: FrameworkDataClient,
  options: LoadFrameworkOptions,
): Promise<ResolvedFramework> {
  const { tenant, versionIdentifier } = options;

  // 1. The version row: resolution key is (owning_tenant, version_identifier),
  //    status filter excludes draft at the query (never post-hoc).
  let versionQuery = client
    .from('framework_versions')
    .select('*')
    .eq('version_identifier', versionIdentifier)
    .in('status', RESOLVABLE_STATUSES);
  versionQuery = tenant == null
    ? versionQuery.is('owning_tenant', null)
    : versionQuery.eq('owning_tenant', tenant);
  const versionResult = await versionQuery.maybeSingle();
  if (versionResult.error) {
    throw new FrameworkLoadError(
      `framework_versions read failed: ${versionResult.error.message}`,
    );
  }
  if (versionResult.data == null) {
    throw new FrameworkLoadError(
      `Framework version not resolvable: identifier=${versionIdentifier} tenant=${tenant ?? 'reference'} (draft versions never resolve; check status and tenant)`,
    );
  }
  const version: FrameworkVersionRow = FrameworkVersionRowV1.parse(versionResult.data);
  const versionId = version.id;

  // 2. Content rows, one query per table (a resolved version is 4 queries
  //    total regardless of competency count).
  const [competencyRows, behaviourRows, gradeRows, dimensionRows] = await Promise.all([
    client
      .from('competencies')
      .select('*')
      .eq('framework_version_id', versionId)
      .in('status', RESOLVABLE_STATUSES)
      .then((r) => rows(r, 'competencies read failed')),
    client
      .from('observable_behaviours')
      .select('*')
      .eq('framework_version_id', versionId)
      .in('status', RESOLVABLE_STATUSES)
      .then((r) => rows(r, 'observable_behaviours read failed')),
    client
      .from('framework_grade_scale')
      .select('*')
      .eq('framework_version_id', versionId)
      .then((r) => rows(r, 'framework_grade_scale read failed')),
    client
      .from('framework_dimension_scales')
      .select('*')
      .eq('framework_version_id', versionId)
      .then((r) => rows(r, 'framework_dimension_scales read failed')),
  ]);

  const competencies: CompetencyRow[] = competencyRows.map((r) => CompetencyRowV1.parse(r));
  const behaviours: ObservableBehaviourRow[] = behaviourRows.map((r) =>
    ObservableBehaviourRowV1.parse(r),
  );
  const gradeScale = gradeRows
    .map((r) => FrameworkGradeScaleRowV1.parse(r))
    .sort((a, b) => a.grade_value - b.grade_value)
    .map((r) => ({ value: r.grade_value, label: r.label, meaning: r.meaning }));
  const dimensionScales = dimensionRows
    .map((r) => FrameworkDimensionScaleRowV1.parse(r))
    .sort((a, b) =>
      a.dimension === b.dimension
        ? a.position - b.position
        : a.dimension.localeCompare(b.dimension),
    )
    .map((r) => ({
      dimension: r.dimension,
      position: r.position,
      code: r.code,
      wordPicture: r.word_picture,
    }));

  // 3. Order competencies by array_position over the version row's
  //    listing_order (fact 3). A code absent from listing_order sorts last
  //    (array_position NULL, ASC NULLS LAST); a listing_order code with no
  //    competency row simply does not appear.
  const listingOrder = version.listing_order;
  const positionOf = (code: string): number => {
    const idx = listingOrder.indexOf(code);
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
  };
  const orderedCompetencies = [...competencies].sort(
    (a, b) => positionOf(a.code) - positionOf(b.code),
  );

  // 4. Nest behaviours per competency; resolve parent codes for derived rows.
  const behaviourCodeById = new Map<string, string>();
  for (const b of behaviours) behaviourCodeById.set(b.id, b.code);
  const behavioursByCompetency = new Map<string, ResolvedBehaviour[]>();
  for (const b of behaviours) {
    const resolved: ResolvedBehaviour = {
      code: b.code,
      behaviour: b.behaviour,
      icaoRef: b.icao_ref,
      origin: b.origin,
      parentCode: b.parent_behaviour_id
        ? behaviourCodeById.get(b.parent_behaviour_id) ?? null
        : null,
      ksaDimension: b.ksa_dimension,
      observableIndicator: b.observable_indicator,
      status: b.status as ResolvedBehaviour['status'],
    };
    const list = behavioursByCompetency.get(b.competency_id);
    if (list) list.push(resolved);
    else behavioursByCompetency.set(b.competency_id, [resolved]);
  }
  // Stable, human order within a competency: numeric suffix where present.
  const behaviourSortKey = (code: string): number => {
    const match = /-(\d+(?:\.\d+)?)$/.exec(code);
    const suffix = match?.[1];
    return suffix !== undefined ? parseFloat(suffix) : Number.MAX_SAFE_INTEGER;
  };
  for (const list of behavioursByCompetency.values()) {
    list.sort((a, b) => behaviourSortKey(a.code) - behaviourSortKey(b.code));
  }

  const resolvedCompetencies: ResolvedCompetency[] = orderedCompetencies.map(
    (c, index) => ({
      code: c.code,
      title: c.title,
      wmLabel: c.wm_label,
      scope: c.scope,
      justification: c.justification,
      listingPosition: index + 1,
      status: c.status as ResolvedCompetency['status'],
      behaviours: behavioursByCompetency.get(c.id) ?? [],
    }),
  );

  return {
    frameworkId: version.framework_id,
    versionIdentifier: version.version_identifier as string,
    versionLabel: version.version_label,
    status: version.status as ResolvedFramework['status'],
    owningTenant: version.owning_tenant,
    listingOrder,
    competencies: resolvedCompetencies,
    competencyCodes: resolvedCompetencies.map((c) => c.code),
    behaviourCodes: resolvedCompetencies.flatMap((c) =>
      c.behaviours.map((b) => b.code),
    ),
    gradeScale,
    dimensionScales,
  };
}

// ---------------------------------------------------------------------------
// Effective-version resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the framework version currently in force for a tenant: the
 * tenant's own newest published version when one exists, otherwise the
 * WingMentor reference model's published version. Expired versions are
 * never "effective" (they resolve only when named explicitly, GCH-18).
 */
export async function resolveEffectiveFrameworkVersion(
  client: FrameworkDataClient,
  tenant?: string | null,
): Promise<ResolvedFramework> {
  const pickNewest = (candidates: FrameworkVersionRow[]): FrameworkVersionRow | null => {
    if (candidates.length === 0) return null;
    const sorted = [...candidates].sort((a, b) => {
      const aKey = a.effective_date ?? a.created_at;
      const bKey = b.effective_date ?? b.created_at;
      return bKey.localeCompare(aKey);
    });
    return sorted[0] ?? null;
  };

  if (tenant != null) {
    const tenantResult = await client
      .from('framework_versions')
      .select('*')
      .eq('owning_tenant', tenant)
      .eq('status', 'published');
    const tenantRows = rows(tenantResult, 'framework_versions read failed').map((r) =>
      FrameworkVersionRowV1.parse(r),
    );
    const newest = pickNewest(tenantRows);
    if (newest && newest.version_identifier) {
      return loadFrameworkVersion(client, {
        tenant,
        versionIdentifier: newest.version_identifier,
      });
    }
  }

  const referenceResult = await client
    .from('framework_versions')
    .select('*')
    .is('owning_tenant', null)
    .eq('status', 'published');
  const referenceRows = rows(referenceResult, 'framework_versions read failed').map((r) =>
    FrameworkVersionRowV1.parse(r),
  );
  const newestReference = pickNewest(referenceRows);
  if (!newestReference || !newestReference.version_identifier) {
    throw new FrameworkLoadError(
      'No published reference framework version found (expected the migration 233 seed)',
    );
  }
  return loadFrameworkVersion(client, {
    tenant: null,
    versionIdentifier: newestReference.version_identifier,
  });
}

/**
 * Resolve the framework version an assessment RECORDS (migration 234 stamps
 * assessments with a version identifier but not the owning tenant): try the
 * tenant's own version first, then the reference model (owning_tenant NULL).
 * Published and expired versions both resolve (GCH-18).
 */
export async function loadRecordedFrameworkVersion(
  client: FrameworkDataClient,
  versionIdentifier: string,
  tenant?: string | null,
): Promise<ResolvedFramework> {
  if (tenant != null) {
    try {
      return await loadFrameworkVersion(client, { tenant, versionIdentifier });
    } catch {
      // Fall through to the reference model.
    }
  }
  return loadFrameworkVersion(client, { tenant: null, versionIdentifier });
}

export { FrameworkLoadError };
